import { useMemo, useState } from 'react';
import styles from "./IntuitButton.module.scss";
import { useStore, useStoreActions } from "../../store/store-context";
import { handlePromise } from "../../infra/utils";
import { useUtmsParams } from "../../hooks/useUtmsParams";
import { useEventReporter } from "../../event-reporter";
import { redirect, routes } from "../../constants";
import { Spinner } from 'seagull/components/Spinner';
import { useLocation } from 'react-router';
import { useIoContext, useOverlayContext } from "../../contexts";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { jsx as _jsx } from "react/jsx-runtime";
export default function IntuitButton(props) {
  const {
    mode,
    ensureEmail
  } = props;
  const {
    NEW_QBO_LOGIN,
    SET_IDENTITY_ONLY_TOKEN_FE
  } = useFeatureFlags();
  const {
    api,
    logBI
  } = useStoreActions();
  const {
    showErrorToast
  } = useOverlayContext();
  const {
    reportToGtm,
    reportToAppcues,
    reportToHubspot,
    reportToHeap
  } = useEventReporter();
  const {
    pathname,
    search
  } = useLocation();
  const {
    changeLocation
  } = useIoContext();
  const {
    mgmtHost
  } = useStore(state => state.session);
  const getUtms = useUtmsParams();
  const [isLoading, setIsLoading] = useState(false);
  const returnTo = useMemo(() => redirect.getReturnToFromSearch(search), [search]);
  const handleSignIn = async () => {
    const {
      origin,
      protocol
    } = window.location;
    setIsLoading(true);
    logBI(11802);
    logBI(18904);
    reportToGtm('button-click', 'intuit-sign-in');
    reportToAppcues('button-click', 'intuit-sign-in');
    reportToHubspot('intuit_sign_in');
    reportToHeap('signIn', 'click', 'intuit-sign-in');
    const utms = getUtms();
    const redirectUrl = returnTo ? "".concat(origin).concat(redirect.getPathAndSearch({
      returnTo
    })) : "".concat(origin).concat(routes.home);
    let request;
    if (NEW_QBO_LOGIN || SET_IDENTITY_ONLY_TOKEN_FE) {
      request = mode === 'signUp' ? api.auth.qboSignUp({
        redirectUrl,
        utms
      }) : api.auth.qboSignIn({
        redirectUrl,
        utms,
        ensureEmail: ensureEmail ? {
          email: ensureEmail,
          errorUrl: "".concat(location.origin).concat(pathname).concat(routes.loginError)
        } : undefined
      });
    } else {
      request = api.auth.intuitSignIn({
        utms,
        redirectUrl,
        authDomain: window._authDomain,
        ensureEmail: ensureEmail ? {
          email: ensureEmail,
          errorUrl: "".concat(origin).concat(pathname).concat(routes.loginError)
        } : undefined
      });
    }
    const result = await handlePromise(request);
    if (result.success && result.data) {
      const intuitUrl = new URL(result.data);
      if (!IS_PRODUCTION && !IS_CYPRESS) {
        // replacing intuit_uri in order to allow setting the cookie while using dev env
        const redirectUri = new URL("".concat(protocol).concat(mgmtHost, "/qbo-integration/oauth2redirect"));
        redirectUri.searchParams.set('env', 'dev');
        intuitUrl.searchParams.set('redirect_uri', redirectUri.toString());
      }
      changeLocation(intuitUrl.toString());
    } else {
      showErrorToast('Failed to continue with Intuit', 'failed-to-continue-with-intuit-toast');
      setIsLoading(false);
    }
  };
  return /*#__PURE__*/_jsx("div", {
    className: styles.buttonContainer,
    children: /*#__PURE__*/_jsx("button", {
      "data-testid": "intuit-sign-in",
      className: styles.button,
      type: "button",
      onClick: handleSignIn,
      children: isLoading ? /*#__PURE__*/_jsx("div", {
        "data-testid": "intuit-sign-in-loading",
        children: /*#__PURE__*/_jsx(Spinner, {
          size: 26
        })
      }) : /*#__PURE__*/_jsx("svg", {
        xmlns: "http://www.w3.org/2000/svg",
        width: "161",
        height: "36",
        viewBox: "0 0 161 36",
        fill: "none",
        children: /*#__PURE__*/_jsx("path", {
          xmlns: "http://www.w3.org/2000/svg",
          d: "M24.4844 13.6C23.6204 12.784 22.3244 12.384 21.1884 12.384C19.1404 12.384 17.0444 13.408 17.0444 15.712C17.0444 17.824 18.5324 18.496 19.9084 18.928C21.3324 19.376 22.5164 19.712 22.5164 20.896C22.5164 22.112 21.4124 22.576 20.4684 22.576C19.5724 22.576 18.5484 22.096 17.9884 21.328L16.5484 22.672C17.4444 23.728 18.9644 24.288 20.4364 24.288C22.6604 24.288 24.5644 23.104 24.5644 20.672C24.5644 18.448 22.7084 17.808 21.2524 17.36C19.9724 16.96 19.0924 16.656 19.0924 15.616C19.0924 14.512 20.1644 14.064 21.0924 14.064C21.8924 14.064 22.7404 14.48 23.1724 15.04L24.4844 13.6ZM26.5199 24H28.4399V16.32H26.5199V24ZM26.2479 13.712C26.2479 14.32 26.7599 14.864 27.4639 14.864C28.1679 14.864 28.7119 14.368 28.7119 13.712C28.7119 13.056 28.1679 12.56 27.4639 12.56C26.7599 12.56 26.2479 13.104 26.2479 13.712ZM36.7532 16.32V17.472H36.7212C36.1612 16.544 35.1532 16.096 34.0812 16.096C31.7612 16.096 30.3212 17.904 30.3212 20.128C30.3212 22.304 31.8092 24.128 34.1132 24.128C35.1692 24.128 36.0652 23.728 36.6252 22.976H36.6572V23.536C36.6572 25.056 36.0812 26.176 34.2252 26.176C33.2652 26.176 32.2732 25.792 31.5372 25.088L30.4332 26.576C31.4252 27.392 32.9932 27.84 34.2412 27.84C37.2332 27.84 38.5772 26.08 38.5772 23.312V16.32H36.7532ZM34.4812 17.664C35.8732 17.664 36.7532 18.784 36.7532 20.112C36.7532 21.392 35.8892 22.544 34.4652 22.544C33.0892 22.544 32.2732 21.328 32.2732 20.112C32.2732 18.752 33.0892 17.664 34.4812 17.664ZM40.8786 24H42.7986V19.776C42.7986 18.752 43.3746 17.696 44.5906 17.696C45.8226 17.696 46.0306 18.832 46.0306 19.744V24H47.9506V19.2C47.9506 17.632 47.1186 16.096 45.1186 16.096C43.9826 16.096 43.0706 16.784 42.7346 17.552H42.7026V16.32H40.8786V24ZM54.2231 24H56.1431V16.32H54.2231V24ZM53.9511 13.712C53.9511 14.32 54.4631 14.864 55.1671 14.864C55.8711 14.864 56.4151 14.368 56.4151 13.712C56.4151 13.056 55.8711 12.56 55.1671 12.56C54.4631 12.56 53.9511 13.104 53.9511 13.712ZM58.4723 24H60.3923V19.776C60.3923 18.752 60.9683 17.696 62.1843 17.696C63.4163 17.696 63.6243 18.832 63.6243 19.744V24H65.5443V19.2C65.5443 17.632 64.7123 16.096 62.7123 16.096C61.5763 16.096 60.6643 16.784 60.3283 17.552H60.2963V16.32H58.4723V24ZM73.3688 24H75.3208L77.0488 18.624H77.0808L78.8248 24H80.7768L83.4328 16.32H81.4328L79.8008 21.824H79.7688L78.1048 16.32H76.0568L74.4088 21.824H74.3768L72.7768 16.32H70.6968L73.3688 24ZM84.6762 24H86.5962V16.32H84.6762V24ZM84.4042 13.712C84.4042 14.32 84.9162 14.864 85.6202 14.864C86.3242 14.864 86.8682 14.368 86.8682 13.712C86.8682 13.056 86.3242 12.56 85.6202 12.56C84.9162 12.56 84.4042 13.104 84.4042 13.712ZM89.1654 17.856V21.44C89.1654 22.912 89.6294 24.16 91.6614 24.16C92.0934 24.16 92.6054 24.08 92.9414 23.952L92.8774 22.448C92.6534 22.56 92.3174 22.608 92.0614 22.608C91.2134 22.608 91.0534 22.112 91.0534 21.424V17.856H92.9734V16.32H91.0534V14.096H89.1654V16.32H87.8214V17.856H89.1654ZM98.6349 16.096C97.4989 16.096 96.6509 16.752 96.3469 17.392H96.3149V11.904H94.3949V24H96.3149V19.808C96.3149 18.784 96.9069 17.744 98.1229 17.744C99.3549 17.744 99.5469 18.864 99.5469 19.776V24H101.467V19.2C101.467 17.632 100.635 16.096 98.6349 16.096ZM107.803 24H109.819V12.672H107.803V24ZM112.222 24H114.142V19.776C114.142 18.752 114.718 17.696 115.934 17.696C117.166 17.696 117.374 18.832 117.374 19.744V24H119.294V19.2C119.294 17.632 118.462 16.096 116.462 16.096C115.326 16.096 114.414 16.784 114.078 17.552H114.046V16.32H112.222V24ZM121.775 17.856V21.44C121.775 22.912 122.239 24.16 124.271 24.16C124.703 24.16 125.215 24.08 125.551 23.952L125.487 22.448C125.263 22.56 124.927 22.608 124.671 22.608C123.823 22.608 123.663 22.112 123.663 21.424V17.856H125.583V16.32H123.663V14.096H121.775V16.32H120.431V17.856H121.775ZM133.98 16.32H132.06V20.544C132.06 21.568 131.484 22.624 130.268 22.624C129.036 22.624 128.828 21.488 128.828 20.576V16.32H126.908V21.12C126.908 22.688 127.74 24.224 129.74 24.224C130.876 24.224 131.788 23.536 132.124 22.768H132.156V24H133.98V16.32ZM136.317 24H138.237V16.32H136.317V24ZM136.045 13.712C136.045 14.32 136.557 14.864 137.261 14.864C137.965 14.864 138.509 14.368 138.509 13.712C138.509 13.056 137.965 12.56 137.261 12.56C136.557 12.56 136.045 13.104 136.045 13.712ZM140.806 17.856V21.44C140.806 22.912 141.27 24.16 143.302 24.16C143.734 24.16 144.246 24.08 144.582 23.952L144.518 22.448C144.294 22.56 143.958 22.608 143.702 22.608C142.854 22.608 142.694 22.112 142.694 21.424V17.856H144.614V16.32H142.694V14.096H140.806V16.32H139.462V17.856H140.806Z",
          fill: "#0077C5"
        })
      })
    })
  });
}